import { Directive, ElementRef, Renderer2, inject } from '@angular/core';

/**
 * Normaliza el valor de un input de celular peruano:
 * - Conserva solo dígitos.
 * - Limita a 9 caracteres.
 * - Si el primer carácter (tras filtrar no-dígitos) no es `9`, devuelve cadena vacía.
 *
 * Esta lógica se exporta también como helper puro para poder testearla
 * sin DI, y para que pueda reutilizarse fuera de la directiva (validadores
 * reactivos, sanitizers en services, etc.).
 */
export function sanitizePhoneInput(value: string): string {
  if (!value) {
    return '';
  }

  let digitsOnly = '';
  for (const char of value) {
    if (char >= '0' && char <= '9') {
      digitsOnly += char;
    }
  }

  if (digitsOnly.length === 0 || digitsOnly[0] !== '9') {
    return '';
  }

  return digitsOnly.length > 9 ? digitsOnly.substring(0, 9) : digitsOnly;
}

/**
 * Sanea en tiempo real el input de un campo de celular peruano:
 * - Solo dígitos.
 * - Máximo 9 caracteres.
 * - Si el primer dígito ingresado no es `9`, el campo se vacía.
 *
 * Tras ajustar el valor del DOM, vuelve a disparar un evento `input` para
 * que los Reactive Forms detecten el cambio.
 */
@Directive({
  selector: '[appPhoneValidator]',
  standalone: true,
  host: {
    '(input)': 'onInputChange()',
  },
})
export class PhoneValidatorDirective {
  private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);

  onInputChange(): void {
    const input = this.el.nativeElement;
    const initialValue = input.value;
    const sanitizedValue = sanitizePhoneInput(initialValue);

    if (initialValue === sanitizedValue) {
      return;
    }

    this.renderer.setProperty(input, 'value', sanitizedValue);
    input.dispatchEvent(new Event('input', { bubbles: false }));
  }
}
